import React, { useContext, useState, useEffect, useMemo } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import {
	IGetSubcategoryRestaurantProperties,
	IGetSubcategoryRestaurantPropertiesArguments,
	IItemForGetShortInfo,
} from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { getSubcategoryRestaurantProperties, updateDishesInSubcategory } from 'backend/subcategories';
import { ModalContent } from 'components/ModalWindows/ModalContent';
import FullInfoField from 'components/FullInfoField/FullInfoField';
import Text from 'components/Text/Text';
import { RestaurantSelect } from './RestaurantSelect';
import { DishMultipleSelect } from './DishMultipleSelect';
import style from './index.module.scss';

interface IProps {
	onCloseModal: () => void;
	onFailureEvent?: () => void;
	onShowError: () => void;
	subcategoryID: string;
	customerName: string;
	categoryName: string;
	restaurantsOfSubcategory: IItemForGetShortInfo[];
	restaurantIdForRestaurantProps: Nullable<string>;
}

export const ModalAssignDishesToSubcategory: React.FC<IProps> = ({
	onCloseModal,
	onFailureEvent,
	onShowError,
	subcategoryID,
	customerName,
	categoryName,
	restaurantsOfSubcategory,
	restaurantIdForRestaurantProps,
}) => {
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const [selectedRestaurant, setSelectedRestaurant] = useState<Nullable<string>>(restaurantIdForRestaurantProps);
	const [selectedDishes, setSelectedDishes] = useState<string[]>([]);
	const [isFirstRender, setIsFirstRender] = useState(true);
	const { response, loading, refetch } = useFetchData<
		IGetSubcategoryRestaurantProperties,
		IGetSubcategoryRestaurantPropertiesArguments
	>(selectedRestaurant && subcategoryID ? getSubcategoryRestaurantProperties : null, {
		restaurantId: selectedRestaurant || '',
		subcategoryId: subcategoryID,
	});

	useEffect(() => {
		if (!isFirstRender) {
			refetch();
		} else {
			setIsFirstRender(false);
		}
	}, [selectedRestaurant]);

	useEffect(() => {
		if (response) {
			setSelectedDishes(response.data.dishes.map((dish) => dish.id));
		}
	}, [response]);

	const restaurants = useMemo(
		() =>
			restaurantsOfSubcategory.map((restaurant) => ({
				id: restaurant.id,
				name: restaurant.name || '',
			})),
		[restaurantsOfSubcategory],
	);

	const handleAssignRestaurants = async () => {
		try {
			showLoader();
			await updateDishesInSubcategory({
				subCategoryId: subcategoryID,
				restaurantId: selectedRestaurant || '',
				dishIds: selectedDishes,
			});
			onCloseModal();
		} catch (err) {
			onShowError();
		} finally {
			hideLoader();
		}
	};

	const handleSetSelectedRestaurant = (value: Nullable<string>) => {
		setSelectedRestaurant(value);
		setSelectedDishes([]);
	};

	return (
		<ModalContent
			onConsentEvent={handleAssignRestaurants}
			onFailureEvent={onFailureEvent ? onFailureEvent : onCloseModal}
			heading="assignDishes"
			headerTranslationNameSpace="form"
			translationOfConfirmBtn="saveButton"
			headingAlignment="left"
			centered={false}>
			<div className={style.flex}>
				<div>
					<FullInfoField label="customerLabel" />
					<Text translationKey={customerName} size="s" />
				</div>
				<div>
					<FullInfoField label="subcategoryLabel" />
					<Text translationKey={categoryName} size="s" />
				</div>
			</div>
			<RestaurantSelect
				setSelectedRestaurant={handleSetSelectedRestaurant}
				selectedRestaurant={selectedRestaurant}
				restaurantsOfSubcategory={restaurants}
			/>
			<DishMultipleSelect
				setSelectedDishes={setSelectedDishes}
				selectedDishes={selectedDishes}
				selectedRestaurant={selectedRestaurant}
				restaurantIsLoading={loading}
			/>
		</ModalContent>
	);
};
